import React, { useEffect, useState } from 'react';
import TopBar from '../components/layout/TopBar';
import api from '../utils/api';
import toast from 'react-hot-toast';

/** Who signed up, and the one button that gives them access. */
export default function BetaTestersPage() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);
  const [shown, setShown] = useState(null);   // password to show once, after approve

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get('/beta-admin');
      setRows(Array.isArray(r.data) ? r.data : []);
    } catch (e) { setRows([]); }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const approve = async (row) => {
    if (!window.confirm(`Create an account for ${row.email} and email them their access?`)) return;
    setBusy(row.id);
    try {
      const r = await api.post(`/beta-admin/${row.id}/approve`);
      setRows(rs => rs.map(x => x.id === row.id ? { ...x, status: 'approved' } : x));
      if (r.data.password) setShown({ email: row.email, password: r.data.password, emailed: r.data.emailed });
      toast.success(r.data.emailed ? 'Approved and emailed' : 'Approved — but the email failed');
    } catch (e) {
      toast.error(e.response?.data?.message || 'Could not approve');
    }
    setBusy(null);
  };

  const decline = async (row) => {
    if (!window.confirm(`Archive ${row.email}? No account is created and nothing is sent.`)) return;
    try {
      await api.post(`/beta-admin/${row.id}/decline`);
      setRows(rs => rs.map(x => x.id === row.id ? { ...x, status: 'declined' } : x));
    } catch (e) { toast.error('Failed'); }
  };

  const suspend = async (row, on) => {
    if (!window.confirm(on
      ? `Suspend ${row.email}? They will not be able to sign in.`
      : `Restore access for ${row.email}?`)) return;
    try {
      await api.post(`/beta-admin/${row.id}/suspend`, { suspend: on });
      setRows(rs => rs.map(x => x.id === row.id ? { ...x, is_suspended: on } : x));
      toast.success(on ? 'Suspended' : 'Access restored');
    } catch (e) { toast.error('Failed'); }
  };

  const since = (d) => {
    if (!d) return 'never signed in';
    const days = Math.floor((Date.now() - new Date(d)) / 86400000);
    if (days === 0) return 'signed in today';
    if (days === 1) return 'signed in yesterday';
    return `signed in ${days} days ago`;
  };

  const chip = (s) => ({
    pending:   { bg: '#FDF3DC', fg: '#9A6A0B', label: 'not confirmed' },
    confirmed: { bg: '#E7EEF6', fg: '#2B5478', label: 'confirmed, waiting' },
    approved:  { bg: '#E4F0E4', fg: '#2C5A2C', label: 'approved' },
    declined:  { bg: '#F0EDE6', fg: '#6B6660', label: 'archived' },
  }[s] || { bg: '#F0EDE6', fg: '#6B6660', label: s });

  const waiting = rows.filter(r => r.status === 'pending' || r.status === 'confirmed').length;

  return (
    <div>
      <TopBar title="Beta testers" subtitle={`${waiting} waiting on you`} />
      <div className="container" style={{ paddingBottom: 60 }}>

        {loading && <div className="muted">Loading…</div>}
        {!loading && rows.length === 0 && (
          <div className="card"><div className="muted">Nobody has signed up yet.</div></div>
        )}

        {rows.map(r => {
          const c = chip(r.status);
          const done = r.status === 'approved' || r.status === 'declined';
          return (
            <div key={r.id} className="card" style={{ marginBottom: 12, opacity: done ? 0.7 : 1 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between',
                            alignItems: 'flex-start', gap: 10 }}>
                <div>
                  <strong>{r.name || r.email}</strong>
                  <div className="muted" style={{ fontSize: 13 }}>{r.email}</div>
                  <div className="muted" style={{ fontSize: 12, marginTop: 3 }}>
                    {r.role || 'role not given'}
                    {r.is_customer ? ' · store customer' : ''}
                    {' · '}{new Date(r.created_at).toLocaleDateString()}
                    {r.user_id ? ' · already has an account' : ''}
                  </div>
                </div>
                <span style={{ background: c.bg, color: c.fg, borderRadius: 6,
                               padding: '3px 9px', fontSize: 11, whiteSpace: 'nowrap' }}>
                  {c.label}
                </span>
              </div>

              {(r.horse_count || r.discipline || r.has_staff || r.current_tool) && (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 10 }}>
                  {[r.horse_count && r.horse_count + ' horses',
                    r.discipline,
                    r.has_staff ? 'staff: ' + (r.staff_count || 'yes') : 'no staff',
                    r.current_tool && 'uses ' + r.current_tool.toLowerCase(),
                  ].filter(Boolean).map(tag => (
                    <span key={tag} style={{ background: 'var(--surface-2, #F3EEE6)',
                      border: '1px solid var(--line, #E4DCCE)', borderRadius: 11,
                      padding: '3px 9px', fontSize: 11, color: 'var(--text-secondary)' }}>{tag}</span>
                  ))}
                </div>
              )}

              {r.biggest_pain && (
                <p style={{ marginTop: 10, fontSize: 14, fontStyle: 'italic' }}>
                  &ldquo;{r.biggest_pain}&rdquo;
                </p>
              )}

              {!done && (
                <div style={{ display: 'flex', gap: 8, marginTop: 12, flexWrap: 'wrap' }}>
                  <button className="btn btn-primary btn-sm" disabled={busy === r.id}
                          onClick={() => approve(r)}>
                    {busy === r.id ? 'Setting up…' : 'Approve and send access'}
                  </button>
                  <button className="btn btn-secondary btn-sm" onClick={() => decline(r)}>
                    Archive
                  </button>
                </div>
              )}

              {r.status === 'approved' && r.user_id && (
                <div style={{ marginTop: 10, paddingTop: 10,
                              borderTop: '1px solid var(--line, #EDE7DC)' }}>
                  <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', fontSize: 13 }}>
                    <span><strong>{r.horse_rows || 0}</strong> <span className="muted">horses added</span></span>
                    <span><strong>{r.reports || 0}</strong> <span className="muted">reports sent</span></span>
                    <span className="muted">{since(r.last_login)}</span>
                  </div>
                  <div style={{ display: 'flex', gap: 8, marginTop: 10, flexWrap: 'wrap' }}>
                    {r.is_suspended
                      ? <button className="btn btn-secondary btn-sm" onClick={() => suspend(r, false)}>Restore access</button>
                      : <button className="btn btn-secondary btn-sm" onClick={() => suspend(r, true)}>Suspend access</button>}
                    {r.is_suspended && <span className="muted" style={{ fontSize: 12, alignSelf: 'center' }}>
                      suspended — cannot sign in</span>}
                  </div>
                  {(r.horse_rows || 0) === 0 && (r.reports || 0) === 0 && r.last_login && (
                    <div className="muted" style={{ fontSize: 12, marginTop: 8 }}>
                      Signed in but has not added a horse or sent a report yet.
                    </div>
                  )}
                </div>
              )}

              {r.status === 'pending' && (
                <div className="muted" style={{ fontSize: 12, marginTop: 8 }}>
                  They never clicked the confirmation email. You can approve anyway.
                </div>
              )}
            </div>
          );
        })}
      </div>

      {shown && (
        <div onClick={() => setShown(null)}
             style={{ position: 'fixed', inset: 0, background: 'rgba(30,3,17,.5)', zIndex: 1000,
                      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
          <div className="card" style={{ maxWidth: 420, width: '100%' }}
               onClick={e => e.stopPropagation()}>
            <h3 style={{ marginBottom: 6 }}>Account created</h3>
            <p className="muted" style={{ fontSize: 13, marginBottom: 12 }}>
              {shown.emailed
                ? 'Their welcome email is on its way. This password is also below in case they ask.'
                : 'The welcome email failed to send — you will need to pass this on yourself.'}
            </p>
            <div style={{ background: 'var(--surface-2, #F3EEE6)', borderRadius: 8, padding: 12 }}>
              <div className="muted" style={{ fontSize: 11 }}>EMAIL</div>
              <div style={{ marginBottom: 8 }}>{shown.email}</div>
              <div className="muted" style={{ fontSize: 11 }}>TEMPORARY PASSWORD</div>
              <div style={{ fontFamily: 'monospace', fontSize: 17 }}>{shown.password}</div>
            </div>
            <button className="btn btn-primary btn-full" style={{ marginTop: 14 }}
                    onClick={() => setShown(null)}>Done</button>
          </div>
        </div>
      )}
    </div>
  );
}
